import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BasketDetail } from '@components/templates/marketplace';
import { SITE_URLS } from '@constants';
import { useSEOMetatags } from '@hooks/useSEOMetatags';
import { useData } from '@hooks/useData';
import { useParams } from 'react-router';
import { useGetAllBundles } from '@services/useGetAllBundles';
import { useGetBundleDetail } from '@services/useGetBundleDetail';

export function BasketDetailPage() {
    const { allSettings, bundleDetail, allBundles } = useData();
    const { request: requestBundleDetail } = useGetBundleDetail();
    const { request: requestAllBundles } = useGetAllBundles();
    const { uuid } = useParams();
    const { t, i18n } = useTranslation();

    // unique categories of all products in bundle
    const categories = (bundleDetail?.data?.products?.map((product) => product.category) || []).filter(
        (category, index, self) => self.indexOf(category) === index
    );

    const translations = {
        title: t('promo-basket-detail-component.metatags.title', {
            name: bundleDetail?.data?.name,
            marketplace: allSettings?.data?.marketplaceName,
        }),
        description: t('promo-basket-detail-component.metatags.description', {
            name: bundleDetail?.data?.name,
            type: categories.join(', '),
            marketplace: allSettings?.data?.marketplaceName,
        }),
    };

    useSEOMetatags({
        title: translations.title,
        description: translations.description,
        image: {
            src: bundleDetail?.data?.image?.src ?? '',
            alt: bundleDetail?.data?.name ?? '',
            type: bundleDetail?.data?.image?.type,
            height: bundleDetail?.data?.image?.height,
            width: bundleDetail?.data?.image?.width,
        },
        url: SITE_URLS.promoBasketDetail.replace(':uuid', `${uuid}`),
        index: true,
    });

    useEffect(() => {
        requestBundleDetail({ uuid: uuid || '' });
        requestAllBundles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid, i18n.language]);

    const formattedBundleDetail = {
        name: bundleDetail?.data?.name || '',
        merchants: bundleDetail?.data?.merchants || [],
        rating: bundleDetail?.data?.rating || 0,
        reviews: bundleDetail?.data?.reviews || 0,
        oldPrice: bundleDetail?.data?.oldPrice ?? null,
        price: bundleDetail?.data?.price || 0,
        points: bundleDetail?.data?.points || 0,
        image: { src: bundleDetail?.data?.image?.src || '' },
        description: bundleDetail?.data?.description || '',
        products: (bundleDetail?.data?.products || []).map((product) => ({
            uuid: product.uuid || '',
            merchant: product.merchant || '',
            name: product.name || '',
            image: product.image || '',
            price: product.price || 0,
            oldPrice: product.oldPrice || 0,
            promo: product.promo || 0,
            quantity: product.quantity || 0,
            attributes: product.attributes
                ? product.attributes.map((attr) => ({
                      uuid: attr.uuid || '',
                      label: attr.label || '',
                      values: attr.value ? [{ uuid: attr.value.uuid || '', value: attr.value.value || '' }] : [],
                  }))
                : [],
            initialFavorite: product.favorite || false,
        })),
    };

    const formattedPromoBaskets =
        allBundles?.data
            ?.filter((bundle) => bundle.uuid !== uuid)
            .map((bundle) => ({
                uuid: bundle.uuid || '',
                merchants: bundle.merchants || [],
                name: bundle.name || '',
                image: bundle.image?.src || '',
                price: bundle.price || 0,
                oldPrice: bundle.oldPrice || undefined,
            })) || [];

    return <BasketDetail {...formattedBundleDetail} relatedPromoBaskets={formattedPromoBaskets} />;
}
